import { useMemo } from "react";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";

const LIFELINE_Y_START = 80;
const LIFELINE_Y_SPACING = 60;
const LIFELINE_X_SPACING = 200;

const DiagramView = ({ data }) => {
    const { nodes, edges } = useMemo(() => {
        if (!data) {
            return { nodes: [], edges: [] };
        }

        const newNodes = [];
        const newEdges = [];

        // Obtener credenciales en orden específico: primera, middleware, resto
        const credentialOrder = new Map();
        const orderedCredentials = [];

        // Obtener todas las credenciales únicas que aparecen en los mensajes
        const usedCredentials = new Set();
        data.event_message_types.forEach((emt) => {
            if (emt.source_credential)
                usedCredentials.add(emt.source_credential);
            if (emt.destination_credential)
                usedCredentials.add(emt.destination_credential);
        });

        // Encontrar credenciales: primera, middleware, otras
        let firstCredential = null;
        let middlewareCredential = null;
        const otherCredentials = [];

        data.credentials.forEach((cred) => {
            const isMiddleware =
                cred.code.toLowerCase().includes("middleware") ||
                cred.name.toLowerCase().includes("middleware");

            if (isMiddleware || usedCredentials.has(cred.code)) {
                if (isMiddleware) {
                    middlewareCredential = cred;
                } else if (!firstCredential) {
                    firstCredential = cred;
                } else {
                    otherCredentials.push(cred);
                }
            }
        });

        // Construir el orden: primera, middleware, otras
        let index = 0;
        if (firstCredential) {
            orderedCredentials.push(firstCredential);
            credentialOrder.set(firstCredential.code, index++);
        }
        if (middlewareCredential) {
            orderedCredentials.push(middlewareCredential);
            credentialOrder.set(middlewareCredential.code, index++);
        }
        otherCredentials.forEach((cred) => {
            orderedCredentials.push(cred);
            credentialOrder.set(cred.code, index++);
        });

        console.log("Sorted credentials:", orderedCredentials);

        // Calcular altura final
        const messageCount = data.event_message_types.filter(
            (emt) => emt.source_credential && emt.destination_credential
        ).length;
        const finalY =
            LIFELINE_Y_START + (messageCount + 2) * LIFELINE_Y_SPACING;

        // 1. Crear nodos de cabecera y líneas de vida
        orderedCredentials.forEach((cred, index) => {
            const xPos = index * LIFELINE_X_SPACING + 100;

            // Nodo cabecera con color personalizado si existe
            newNodes.push({
                id: cred.code,
                position: { x: xPos + 10, y: 40 },
                data: { label: cred.name },
                type: "default",
                draggable: false,
                style: {
                    width: 150,
                    height: 40,
                    backgroundColor: cred.color ? cred.color : "#f0f8ff",
                    border: "2px solid #4a90e2",
                    borderRadius: "8px",
                    fontSize: "12px",
                    textAlign: "center",
                },
            });

            // Nodo inicio línea de vida
            newNodes.push({
                id: `${cred.code}-start`,
                position: { x: xPos + 75, y: 60 },
                data: { label: "" },
                draggable: false,
                style: {
                    width: 1,
                    height: 1,
                    opacity: 0,
                    visibility: "hidden",
                },
            });

            // Nodo final línea de vida
            newNodes.push({
                id: `${cred.code}-bottom`,
                position: { x: xPos + 75, y: finalY },
                data: { label: "" },
                draggable: false,
                style: {
                    width: 1,
                    height: 1,
                    opacity: 0,
                    visibility: "hidden",
                },
            });

            // Línea de vida vertical
            newEdges.push({
                id: `lifeline-${cred.code}`,
                source: `${cred.code}-start`,
                target: `${cred.code}-bottom`,
                type: "straight",
                style: {
                    stroke: "#999",
                    strokeWidth: 2,
                    strokeDasharray: "8,4",
                },
            });
        });

        // 2. Crear mensajes
        const validMessages = data.event_message_types.filter(
            (emt) => emt.source_credential && emt.destination_credential
        );

        validMessages.forEach((emt, messageIndex) => {
            const sourceIndex = credentialOrder.get(emt.source_credential);
            const targetIndex = credentialOrder.get(emt.destination_credential);
            const middlewareIndex = middlewareCredential
                ? credentialOrder.get(middlewareCredential.code)
                : null;

            if (
                sourceIndex !== undefined &&
                targetIndex !== undefined &&
                middlewareIndex !== undefined
            ) {
                const messageY =
                    LIFELINE_Y_START + (messageIndex + 1) * LIFELINE_Y_SPACING;
                const sourceX = sourceIndex * LIFELINE_X_SPACING + 175;
                const middlewareX = middlewareIndex * LIFELINE_X_SPACING + 175;
                const targetX = targetIndex * LIFELINE_X_SPACING + 175;

                // Buscar color del origen
                let arrowColor = "#e74c3c";
                const sourceCred = orderedCredentials[sourceIndex];
                if (sourceCred && sourceCred.color) {
                    arrowColor = sourceCred.color;
                }

                // PRIMER MENSAJE: Origen -> Middleware
                newNodes.push({
                    id: `src1-${emt.id}`,
                    position: { x: sourceX, y: messageY },
                    data: { label: "" },
                    type: "default",
                    draggable: false,
                    style: {
                        width: 1,
                        height: 1,
                        opacity: 0,
                        visibility: "hidden",
                    },
                });

                newNodes.push({
                    id: `mid1-${emt.id}`,
                    position: { x: middlewareX, y: messageY + 24 },
                    data: { label: "" },
                    type: "default",
                    draggable: false,
                    style: {
                        width: 1,
                        height: 1,
                        opacity: 0,
                        visibility: "hidden",
                    },
                });

                newEdges.push({
                    id: `message1-${emt.id}`,
                    source: `src1-${emt.id}`,
                    target: `mid1-${emt.id}`,
                    label: emt.event_type_code,
                    type: "straight",
                    style: { stroke: arrowColor, strokeWidth: 2 },
                    markerEnd: { type: "arrowclosed", color: arrowColor },
                    labelStyle: {
                        fontSize: "11px",
                        fontWeight: "bold",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                    },
                });

                // SEGUNDO MENSAJE: Middleware -> Destino
                newNodes.push({
                    id: `mid2-${emt.id}`,
                    position: { x: middlewareX, y: messageY + 24 },
                    data: { label: "" },
                    type: "default",
                    draggable: false,
                    style: {
                        width: 1,
                        height: 1,
                        opacity: 0,
                        visibility: "hidden",
                    },
                });

                newNodes.push({
                    id: `tgt2-${emt.id}`,
                    position: { x: targetX, y: messageY + 48 },
                    data: { label: "" },
                    type: "default",
                    draggable: false,
                    style: {
                        width: 1,
                        height: 1,
                        opacity: 0,
                        visibility: "hidden",
                    },
                });

                newEdges.push({
                    id: `message2-${emt.id}`,
                    source: `mid2-${emt.id}`,
                    target: `tgt2-${emt.id}`,
                    label: emt.event_type_code,
                    type: "straight",
                    style: { stroke: arrowColor, strokeWidth: 2 },
                    markerEnd: { type: "arrowclosed", color: arrowColor },
                    labelStyle: {
                        fontSize: "11px",
                        fontWeight: "bold",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                    },
                });
            }
        });

        console.log("Final nodes:", newNodes.length);
        console.log("Final edges:", newEdges.length);

        return { nodes: newNodes, edges: newEdges };
    }, [data]);

    // Mostrar mensaje si no hay datos
    if (!data) {
        return (
            <div
                style={{
                    height: "100vh",
                    width: "100vw",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <h2>No hay datos disponibles para el diagrama</h2>
            </div>
        );
    }

    return (
        <div style={{ height: "100vh", width: "100vw", overflow: "hidden" }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                fitView
                fitViewOptions={{
                    padding: 0.1,
                    includeHiddenNodes: false,
                    minZoom: 0.5,
                    maxZoom: 1.5,
                }}
                nodesDraggable={false}
                nodesConnectable={false}
                panOnScroll={true}
                panOnScrollMode="vertical"
                preventScrolling={false}
                attributionPosition="bottom-left"
            >
                <Background variant="dots" gap={20} size={1} color="#e1e1e1" />
                <Controls showInteractive={false} position="top-right" />
            </ReactFlow>
        </div>
    );
};

export default DiagramView;
